// src/components/SuperAdmin/BrandSettings.js
import React, { useEffect, useState } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { Backsheet, Button, FormInput } from '../ui/UIComponents';
import { Edit2 } from 'lucide-react';

const COLLECTION = 'buzztobrand';
const DOC_ID = 'settings';

const SECTIONS = {
    identity: {
        title: 'Brand Identity',
        fields: ['brand_name', 'brand_logo'],
    },
    social: {
        title: 'Social Links',
        fields: ['facebook_link', 'instagram_link', 'linkedin_link', 'youtube_link', 'twitter_link'],
    },
    contact: {
        title: 'Contact Info',
        fields: ['whatsapp_link', 'gmail_link', 'phone_link'],
    },
    messaging: {
        title: 'Messaging Defaults',
        fields: ['whatsapp_default_message', 'disclaimer'],
    },
};

export default function BrandSettings() {
    const db = getFirestore();
    const ref = doc(db, COLLECTION, DOC_ID);

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});

    // Fetch the doc
    const fetchDetails = async () => {
        setLoading(true);
        const snap = await getDoc(ref);
        setData(snap.exists() ? snap.data() : {});
        setLoading(false);
    };

    useEffect(() => {
        fetchDetails();
    }, []);

    const openEdit = sectionKey => {
        const initial = {};
        SECTIONS[sectionKey].fields.forEach(f => {
            initial[f] = data[f] || '';
        });
        setForm(initial);
        setEditing(sectionKey);
    };

    const closeEdit = () => {
        setEditing(null);
        setForm({});
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        await setDoc(ref, form, { merge: true });
        await fetchDetails();
        closeEdit();
    };

    if (loading) return <p>Loading brand settings…</p>;

    return (
        <div className="min-h-screen space-y-8">
            {Object.entries(SECTIONS).map(([key, { title, fields }]) => (
                <section key={key} className="bg-white p-4 rounded shadow">
                    <header className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold">{title}</h3>
                        <button
                            onClick={() => openEdit(key)}
                            aria-label={`Edit ${title}`}
                            title={`Edit ${title}`}
                            className="
                        p-2 
                        bg-blue-500 text-white 
                        rounded-full 
                        hover:bg-blue-600 
                        focus:outline-none focus:ring-2 focus:ring-blue-300 
                        transition
                    "
                        >
                            <Edit2 className="w-5 h-5" />
                        </button>

                    </header>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                        {fields.map(f => (
                            <div key={f} className="flex">
                                <span className="font-bold text-indigo-700 capitalize">{f.replace(/_/g, ' ')}:</span>
                                <span className="ml-2 truncate">{data[f] || ''}</span>
                            </div>
                        ))}
                    </div>
                </section>
            ))}

            <Backsheet
                isOpen={!!editing}
                onClose={closeEdit}
                title={editing ? `Edit ${SECTIONS[editing].title}` : ''}
            >
                <div className="space-y-4 p-4">
                    {editing && SECTIONS[editing].fields.map(field => (
                        <FormInput
                            key={field}
                            label={field.replace(/_/g, ' ').toUpperCase()}
                            name={field}
                            value={form[field] || ''}
                            onChange={handleChange}
                        />
                    ))}

                    <div className="flex justify-end space-x-2 pt-4">
                        <Button variant="secondary" onClick={closeEdit}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                </div>
            </Backsheet>
        </div>
    );
}
